import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Paper, Button, Avatar, Fade, AppBar, Toolbar, IconButton, Dialog, Drawer } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useDeviceInfo from '../../utils/useDeviceInfo';
import { registerForPushNotifications } from '../../utils/PushNotificationService';
import AddContact from '../../components/profile/friends/AddContact';
import { Share } from '@capacitor/share';
import VideoPlayer from '../../components/VideoPlayer'; 
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next'; // Importer le hook i18n

const onboardingSteps = [
  {
    id: "welcome",
    sender: "Raoul",
    message: "welcome_message", // Utilisation de la clé pour la traduction
    responses: ["start_button"] // Utilisation de la clé pour la traduction
  },
  {
    id: "benefits",
    sender: "Raoul",
    message: "benefits_message", // Utilisation de la clé pour la traduction
    responses: ["understood_button"] // Utilisation de la clé pour la traduction
  },
  {
    id: "meditation_time",
    sender: "Raoul",
    message: "meditation_time_message", // Utilisation de la clé pour la traduction
    responses: ["morning_button", "noon_button", "evening_button"] // Utilisation des clés pour la traduction
  },
  {
    id: "enable_notifications",
    sender: "Raoul",
    message: "enable_notifications_message", // Utilisation de la clé pour la traduction
    responses: ["enable_notifications_button"] // Utilisation de la clé pour la traduction
  },
  {
    id: "add_friends",
    sender: "Raoul",
    message: "add_friends_message", // Utilisation de la clé pour la traduction
    responses: ["add_friends_button"] // Utilisation de la clé pour la traduction
  },
  {
    id: "invite_friends",
    sender: "Raoul",
    message: "invite_friends_message", // Utilisation de la clé pour la traduction
    responses: ["invite_friends_button"] // Utilisation de la clé pour la traduction
  },
  {
    id: "finished",
    sender: "Raoul",
    message: "finished_message", // Utilisation de la clé pour la traduction
    responses: ["thank_you_button"] // Utilisation de la clé pour la traduction
  }
];

const OnboardingChat = () => {
  const { t } = useTranslation(); // Hook pour les traductions
  const [step, setStep] = useState(0);
  const [conversation, setConversation] = useState([{ sender: "Raoul", message: t(onboardingSteps[0].message) }]);
  const [isTyping, setIsTyping] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [openAddFriendsDrawer, setOpenAddFriendsDrawer] = useState(false);
  const [openBenefitsDialog, setOpenBenefitsDialog] = useState(false); // Pour la popin des bénéfices
  const [isVideoOpen, setIsVideoOpen] = useState(false); // Pour ouvrir la vidéo
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();
  const { paddingTop, paddingBottom } = useDeviceInfo();
  const { refreshUser } = useAuth();

  const updateUserData = async (data) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(data)
      });
  
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
  
      const responseData = await response.json();
    } catch (error) {
      console.error("Erreur lors de la mise à jour des données :", error);
    }
  };
  

  const handlePushNotifications = async () => {
    try {
      await registerForPushNotifications();
    } catch (error) {
      console.error("Erreur lors de l'activation des notifications:", error);
    }
  };

  const handleAddFriends = () => {
    setOpenAddFriendsDrawer(true); 
  };

  const handleAddFriendsClose = () => {
    setOpenAddFriendsDrawer(false); 
    proceedToNextStep();
  };

  const handleInviteFriends = async () => {
    try {
      await Share.share({
        title: t('invite_title'),
        text: t('invite_message'),
        url: 'https://www.heyraoul.fr',
        dialogTitle: t('invite_dialog_title')
      });
      proceedToNextStep();
    } catch (error) {
      console.error('Erreur lors de l\'invitation :', error);
      proceedToNextStep();
    }
  };

  const handleCloseVideo = () => {
    setIsVideoOpen(false);
    proceedToNextStep();
  };

  const calculateTypingDelay = (message) => {
    const wordsCount = message.split(" ").length;
    return Math.min(3000, wordsCount * 200);
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleEndClick = () => {
    navigate('/');
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversation, isTyping]);

const handleNextStep = (response) => {
  setButtonDisabled(true);
  const currentStepId = onboardingSteps[step].id;

  // Toujours afficher la réponse utilisateur dans le chat
  setConversation((prev) => [...prev, { sender: "User", message: response }]);
  setIsTyping(true);

  // Si l'utilisateur clique sur "Plus tard", on ne déclenche pas l'action, mais on passe à l'étape suivante
if (response === t('later_button')) {
  const nextStep = onboardingSteps[step + 1];
  const typingDelay = calculateTypingDelay(t(nextStep?.message) || "");

  setTimeout(() => {
    setIsTyping(false);
    if (nextStep) {
      setConversation((prev) => [...prev, { sender: nextStep.sender, message: t(nextStep.message) }]);
      setStep(step + 1);
    }
    setButtonDisabled(false);
  }, typingDelay);

  return;
}


  if (currentStepId === "meditation_time") {
    const selectedTime = response === t("morning_button") ? "morning" : response === t("noon_button") ? "noon" : "evening";
    updateUserData({ meditationTime: selectedTime });
  }

  if (currentStepId === "enable_notifications") {
    handlePushNotifications();
  }

  if (currentStepId === "add_friends") {
    handleAddFriends();
  }

  if (currentStepId === "invite_friends") {
    handleInviteFriends();
  }

  if (currentStepId !== "add_friends" && currentStepId !== "invite_friends") {
    proceedToNextStep();
  }
};


  const proceedToNextStep = () => {
    const nextStep = onboardingSteps[step + 1];
    const typingDelay = calculateTypingDelay(t(nextStep?.message) || "");

    setTimeout(() => {
      setIsTyping(false);
      if (nextStep) {
        setConversation((prev) => [...prev, { sender: nextStep.sender, message: t(nextStep.message) }]);
        setStep(step + 1);
      }
      setButtonDisabled(false);
    }, typingDelay);
  };

return (
  <>
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center', px: 3, mt: 3, pt: paddingTop + 1  }}>
      
      <Box sx={{ width: '100%', maxWidth: 400, overflowY: 'auto', mb: 2, pb: 8 }}>
        {conversation.map((conv, index) => (
          <Fade in={true} key={index} timeout={{ enter: 800 }}>
            <Box sx={{ display: 'flex', mb: 2, justifyContent: conv.sender === "User" ? 'flex-end' : 'flex-start' }}>
              {conv.sender === "Raoul" && (
                <Avatar src="https://data.heyraoul.fr/img/avatars/user/004.jpg" sx={{ width: 44, height: 44, mr: 1.5, border: '1px solid #DDD' }} />
              )}
              <Paper elevation="0" sx={{ px: 2, py: 1.5, backgroundColor: conv.sender === "User" ? 'primary.main' : '#FFF', color: conv.sender === "User" ? '#FFF' : '#000', borderRadius: 4, border: conv.sender === "User" ? '1px solid #000' : '1px solid #DDD', }}>
                <Typography variant="body2">{conv.message}</Typography>
              </Paper>
            </Box>
          </Fade>
        ))}

        {isTyping && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar src="https://data.heyraoul.fr/img/avatars/user/004.jpg" sx={{ width: 44, height: 44, mr: 1.5, border: '1px solid #DDD' }} />
            <Typography variant="body2" sx={{ backgroundColor: '#FFF', borderRadius: '20px', px: 0, py: 1 }}>
              <span className="dot one">•</span><span className="dot two">•</span><span className="dot three">•</span>
            </Typography>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {step < onboardingSteps.length - 1 && (
        <Box sx={{ position: 'fixed', bottom: '-1px', width: '100%', backgroundColor: 'white', border: '0px', pb: paddingBottom + 2, pt: 1.5, px: 3 }}>
          {onboardingSteps[step].responses.length === 3 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mx: -0.5 }}>
              {onboardingSteps[step].responses.map((response, idx) => (
                <Button
                  key={idx}
                  sx={{ mx: 0.5 }}
                  variant="contained"
                  onClick={() => handleNextStep(t(response))}
                  disabled={buttonDisabled}
                >
                  {t(response)}
                </Button>
              ))}
            </Box>
          )}

          {onboardingSteps[step].responses.length === 1 &&
            ["enable_notifications", "add_friends", "invite_friends"].includes(onboardingSteps[step].id) ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
              <Button
                variant="text"
                sx={{ width:"50%" }}
                onClick={() => handleNextStep(t("later_button"))}
                disabled={buttonDisabled}
              >
                {t("later_button")}
              </Button>
              <Button
                variant="contained"
                onClick={() => handleNextStep(t(onboardingSteps[step].responses[0]))}
                disabled={buttonDisabled}
              >
                {t(onboardingSteps[step].responses[0])}
              </Button>
            </Box>
          ) : onboardingSteps[step].responses.length === 1 && (
            <Button
              variant="contained"
              onClick={() => handleNextStep(t(onboardingSteps[step].responses[0]))}
              disabled={buttonDisabled}
            >
              {t(onboardingSteps[step].responses[0])}
            </Button>
          )}
        </Box>
      )}

      <Box sx={{ position: 'fixed', bottom: '-1px', width: '100%', pb: paddingBottom + 2, pt: 1.5, px: 3 }}>
        {step === onboardingSteps.length - 1 && (
          <Button variant="contained" onClick={() => {
            updateUserData({ onboarded: true })
              .then(() => refreshUser())
              .then(() => navigate('/'));
          }}>
            {t('finish')}
          </Button>
        )}
      </Box>

      {/* Popin pour lancer la vidéo à la place des statistiques */}
      <VideoPlayer
        videoUrl="/video/test.mp4"
        open={isVideoOpen}
        onClose={handleCloseVideo}
      />

      {/* Bottom Drawer pour ajouter des amis */}
      <Drawer
        anchor="bottom"
        open={openAddFriendsDrawer}
        onClose={handleAddFriendsClose}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ textAlign: 'center', mb: 2 }}>{t('add_friends')}</Typography>
          <AddContact />
        </Box>
      </Drawer>

      <React.Fragment>
        <style>
          {`
            .dot {
              animation: blink 1.5s infinite;
            }
            .dot.one {
              animation-delay: 0s;
            }
            .dot.two {
              animation-delay: 0.3s;
            }
            .dot.three {
              animation-delay: 0.6s;
            }

            @keyframes blink {
              0% { opacity: 0; }
              50% { opacity: 1; }
              100% { opacity: 0; }
            }
          `}
        </style>
      </React.Fragment>
    </Box>
  </>
);


};

export default OnboardingChat;
